import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faSpinner, faRotate } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const MarketMovers = ({ direction = 'gainers', onSymbolSelect }) => {
  const navigate = useNavigate();
  const [movers, setMovers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMovers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await marketDataApi.getMarketMovers({ direction, top: 10, marketType: 'stocks' });
      setMovers(response.data || []);
    } catch (err) {
      console.error(`Error loading ${direction}:`, err);
      setError(err.response?.data?.error || err.message || `Failed to load ${direction}`);
      setMovers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMovers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const isGainers = direction === 'gainers';
  const title = isGainers ? 'Top Gainers' : 'Top Losers';
  const icon = isGainers ? faArrowUp : faArrowDown;
  const iconColor = isGainers ? 'text-green-600' : 'text-red-600';

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <h2 className={`text-base font-bold text-gray-800 flex items-center gap-1.5 ${iconColor}`}>
          <FontAwesomeIcon icon={icon} className="text-xs" />
          {title}
        </h2>
        <button
          onClick={loadMovers}
          disabled={loading}
          className="px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
        >
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
          ) : (
            <FontAwesomeIcon icon={faRotate} className="text-xs" />
          )}
        </button>
      </div>

      {error && (
        <div className="mb-2 p-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs">
          {error}
        </div>
      )}

      {loading && movers.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-xs">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin mb-2" />
          Loading...
        </div>
      ) : movers.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-xs">No data available</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Symbol</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Price</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Change %</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {movers.map((mover, index) => {
                const changePercent = mover.change_percent || mover.changePercent || 0;
                const isPositive = changePercent >= 0;
                
                return (
                  <tr 
                    key={mover.symbol || index} 
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      if (onSymbolSelect) onSymbolSelect(mover.symbol);
                      else navigate(`/asset/${mover.symbol}`);
                    }}
                  >
                    <td className="px-2 py-1.5 font-semibold text-indigo-600">{mover.symbol}</td>
                    <td className="px-2 py-1.5 text-gray-700">{formatCurrency(mover.price || mover.close || mover.last_price)}</td>
                    <td className={`px-2 py-1.5 font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                      {isPositive ? '+' : ''}{changePercent.toFixed(2)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MarketMovers;

